// src/components/dashboard/groups/GroupDetailsModal.tsx
// ✅ v3 — Classes isoladas com prefixo dtlgrp-

import React from 'react';
import { X, Users, Calendar, Hash, BookOpen, HelpCircle } from 'lucide-react';
import './GroupDetailsModal.css';

interface GroupMember {
    userId: string;
    username: string;
    profilePhoto?: string;
    role: 'admin' | 'member';
    stats: {
        cardsAnswered: number;
        cardsMastered: number;
        questionsAnswered: number;
        questionsCorrect: number;
        studyScore: number;
    };
    joinedAt: Date | string;
}

interface GroupDetailsModalProps {
    isOpen: boolean;
    groupId: string;
    groupName: string;
    groupIcon?: string;
    description?: string;
    inviteCode: string;
    createdAt: Date | string;
    members: GroupMember[];
    onClose: () => void;
}

const GroupDetailsModal: React.FC<GroupDetailsModalProps> = ({
    isOpen, groupName, groupIcon, description, inviteCode, createdAt, members, onClose
}) => {
    if (!isOpen) return null;

    const formatDate = (date: Date | string) =>
        new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

    const sorted = [...members].sort((a, b) => (b.stats.studyScore ?? 0) - (a.stats.studyScore ?? 0));
    const totalScore = sorted.reduce((s, m) => s + (m.stats.studyScore ?? 0), 0);
    const totalCards = sorted.reduce((s, m) => s + (m.stats.cardsMastered ?? 0), 0);
    const totalQuestions = sorted.reduce((s, m) => s + (m.stats.questionsCorrect ?? 0), 0);

    return (
        <div className="dtlgrp-overlay" onClick={onClose}>
            <div className="dtlgrp-modal" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="dtlgrp-header">
                    <div className="dtlgrp-header-left">
                        {groupIcon && (
                            <img src={groupIcon || ''} alt={groupName} className="dtlgrp-icon" />
                        )}
                        <div>
                            <h2 className="dtlgrp-title">{groupName}</h2>
                            {description && <p className="dtlgrp-description">{description}</p>}
                        </div>
                    </div>
                    <button className="dtlgrp-close" onClick={onClose} type="button" aria-label="Fechar">
                        <X size={18} />
                    </button>
                </div>

                {/* Info cards */}
                <div className="dtlgrp-info-grid">
                    <div className="dtlgrp-info-card">
                        <Hash size={14} className="dtlgrp-info-icon" />
                        <span className="dtlgrp-info-label">Código</span>
                        <span className="dtlgrp-info-value dtlgrp-code">{inviteCode}</span>
                    </div>
                    <div className="dtlgrp-info-card">
                        <Calendar size={14} className="dtlgrp-info-icon" />
                        <span className="dtlgrp-info-label">Criado em</span>
                        <span className="dtlgrp-info-value">{formatDate(createdAt)}</span>
                    </div>
                    <div className="dtlgrp-info-card">
                        <Users size={14} className="dtlgrp-info-icon" />
                        <span className="dtlgrp-info-label">Membros</span>
                        <span className="dtlgrp-info-value">{members.length}</span>
                    </div>
                </div>

                {/* Totais */}
                <div className="dtlgrp-totals">
                    <div className="dtlgrp-total-item">
                        <span className="dtlgrp-total-value">{totalScore}</span>
                        <span className="dtlgrp-total-label">pts totais</span>
                    </div>
                    <div className="dtlgrp-total-sep" />
                    <div className="dtlgrp-total-item">
                        <BookOpen size={14} className="dtlgrp-total-icon" />
                        <span className="dtlgrp-total-value">{totalCards}</span>
                        <span className="dtlgrp-total-label">dominados</span>
                    </div>
                    <div className="dtlgrp-total-sep" />
                    <div className="dtlgrp-total-item">
                        <HelpCircle size={14} className="dtlgrp-total-icon" />
                        <span className="dtlgrp-total-value">{totalQuestions}</span>
                        <span className="dtlgrp-total-label">questões certas</span>
                    </div>
                </div>

                {/* Tabela */}
                <div className="dtlgrp-members-section">
                    <h3 className="dtlgrp-section-title">Desempenho dos membros</h3>
                    <div className="dtlgrp-table">
                        <div className="dtlgrp-table-head">
                            <span className="dtlgrp-col-rank">#</span>
                            <span className="dtlgrp-col-member">Membro</span>
                            <span className="dtlgrp-col-stat">Pontuação</span>
                            <span className="dtlgrp-col-stat dtlgrp-flex-label"><BookOpen size={12} />Dominados</span>
                            <span className="dtlgrp-col-stat dtlgrp-flex-label"><HelpCircle size={12} />Acertos</span>
                        </div>

                        {sorted.map((member, index) => (
                            <div key={member.userId} className={`dtlgrp-table-row ${index < 3 ? `dtlgrp-top-${index + 1}` : ''}`}>
                                <span className={`dtlgrp-col-rank dtlgrp-rank-num dtlgrp-rank-${index + 1}`}>
                                    {index + 1}
                                </span>

                                <div className="dtlgrp-col-member">
                                    {member.profilePhoto ? (
                                        <img
                                            src={member.profilePhoto || ''}
                                            alt={member.username}
                                            className="dtlgrp-avatar"
                                        />
                                    ) : (
                                        <div className="dtlgrp-avatar-fallback">
                                            {member.username.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div>
                                        <span className="dtlgrp-member-name">{member.username}</span>
                                        {member.role === 'admin' && <span className="dtlgrp-admin-badge">Admin</span>}
                                    </div>
                                </div>

                                <span className="dtlgrp-col-stat dtlgrp-score-val">
                                    {member.stats.studyScore ?? 0}
                                    <span className="dtlgrp-pts"> pts</span>
                                </span>
                                <span className="dtlgrp-col-stat">{member.stats.cardsMastered ?? 0}</span>
                                <span className="dtlgrp-col-stat">{member.stats.questionsCorrect ?? 0}</span>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default GroupDetailsModal;